import { create } from 'zustand';
import axios from 'axios';
import api from '../services/axios';
import { IApiPayload } from '../pages/publics/Register/utils';
import type { ICurrentUserMe } from '../services/users/types';
import { parseAuthError } from '../services/auth/parseAuthError';
import type { IRegisterResponse, ILoginResponse } from '../services/auth/types';
import {
  clearTokens,
  hasStoredAccessToken,
  hasStoredRefreshToken,
  setTokens,
} from '../services/auth/tokenStorage';
import {
  clearCsrfCache,
  clearSessionLoggedOut,
  fetchCurrentUser,
  fetchCsrfToken,
  hasSessionCredentials,
  logoutSession,
  markSessionLoggedOut,
  refreshSession,
} from '../services/auth/session';
import { registerSessionTeardown } from '../services/auth/sessionTeardown';
import { resetShellQueryPrefetchCache } from '../queries/shellQueries';
import { resetDashboardIndicatorsPrefetchCache } from '../hooks/useDashboardIndicators';
import { clearCustomerSubscriptionCache } from '../utils/customerSubscription';
import { clearAllMyStationAiCache } from '../pages/privates/Dashboard/MyStation/utils/myStationAiCache';
import { AUTH_USE_COOKIES } from '../config/constants';
import { isServerOrNetworkError } from '../utils/serverError';
import { resolveSupportCode } from '../utils/supportCode';
import {
  clearActivityQueue,
  clearActivitySessionId,
  setActivitySessionId,
} from '../services/activity';
import { useActiveCustomerStore } from './useActiveCustomerStore';

interface AuthState {
  loading: boolean;
  sessionLoading: boolean;
  authenticated: boolean;
  user: ICurrentUserMe | null;
  bootstrapServerError: boolean;
  bootstrapSupportCode: string | null;
  setAuthenticated: (authenticated: boolean) => void;
  setUser: (user: ICurrentUserMe | null) => void;
  refreshCurrentUser: () => Promise<void>;
  bootstrapSession: () => Promise<void>;
  retryBootstrapSession: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (userData: IApiPayload) => Promise<IRegisterResponse>;
}

const PUBLIC_AUTH_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/ativar-conta',
  '/verificar-email',
  '/planos',
  '/escolher-plano',
];

const persistActivitySessionId = (sessionId?: string | null): void => {
  if (sessionId) {
    setActivitySessionId(sessionId);
  }
};

const teardownLocalSession = () => {
  markSessionLoggedOut();
  clearCsrfCache();
  clearTokens();
  clearActivitySessionId();
  clearActivityQueue();
  clearCustomerSubscriptionCache();
  clearAllMyStationAiCache();
  resetShellQueryPrefetchCache();
  resetDashboardIndicatorsPrefetchCache();
  useActiveCustomerStore.getState().clear();
  useAuthStore.setState({
    authenticated: false,
    user: null,
    loading: false,
    sessionLoading: false,
    bootstrapServerError: false,
    bootstrapSupportCode: null,
  });

  const path = window.location.pathname;
  const isPublicAuthPage = PUBLIC_AUTH_PATHS.some(
    (publicPath) => path === publicPath || path.startsWith(`${publicPath}/`)
  );
  if (!isPublicAuthPage) {
    window.location.href = '/login';
  }
};

registerSessionTeardown(teardownLocalSession);

let bootstrapSessionInFlight: Promise<void> | null = null;
let lastBootstrapRetryAt = 0;
const MIN_BOOTSTRAP_RETRY_INTERVAL_MS = 5_000;

export const useAuthStore = create<AuthState>()((set) => ({
  loading: false,
  sessionLoading: true,
  authenticated: false,
  user: null,
  bootstrapServerError: false,
  bootstrapSupportCode: null,

  setAuthenticated: (authenticated: boolean) => set({ authenticated }),
  setUser: (user: ICurrentUserMe | null) => set({ user }),

  refreshCurrentUser: async () => {
    const { authenticated } = useAuthStore.getState();
    if (!authenticated || !hasSessionCredentials()) return;
    try {
      const user = await fetchCurrentUser();
      persistActivitySessionId(user.sessionId);
      set({ user });
    } catch (error) {
      // Contexto de posto: manter a sessão atual; o bootstrap trata sessão inválida
      console.error('Erro ao atualizar usuário do posto ativo:', error);
    }
  },

  bootstrapSession: async () => {
    if (bootstrapSessionInFlight) {
      return bootstrapSessionInFlight;
    }

    bootstrapSessionInFlight = (async () => {
      if (!hasSessionCredentials()) {
        set({
          sessionLoading: false,
          authenticated: false,
          user: null,
          bootstrapServerError: false,
          bootstrapSupportCode: null,
        });
        return;
      }

      const hadCredentials = hasSessionCredentials();
      set({ sessionLoading: true, bootstrapServerError: false, bootstrapSupportCode: null });

      try {
        let user: ICurrentUserMe;
        try {
          user = await fetchCurrentUser({ skipAuthRefresh: true });
        } catch (meError) {
          // Access expirado: tenta refresh antes de tratar como sessão inválida
          const isUnauthorized = axios.isAxiosError(meError) && meError.response?.status === 401;
          if (!isUnauthorized) {
            throw meError;
          }
          await refreshSession();
          user = await fetchCurrentUser({ skipAuthRefresh: true });
        }
        persistActivitySessionId(user.sessionId);
        set({
          authenticated: true,
          user,
          sessionLoading: false,
          bootstrapServerError: false,
          bootstrapSupportCode: null,
        });
      } catch (error) {
        console.error('Erro no bootstrap de sessão:', error);
        if (hadCredentials && isServerOrNetworkError(error)) {
          set({
            authenticated: false,
            user: null,
            sessionLoading: false,
            bootstrapServerError: true,
            bootstrapSupportCode: resolveSupportCode(error),
          });
          return;
        }
        clearTokens();
        set({
          authenticated: false,
          user: null,
          sessionLoading: false,
          bootstrapServerError: false,
          bootstrapSupportCode: null,
        });
      }
    })();

    try {
      await bootstrapSessionInFlight;
    } finally {
      bootstrapSessionInFlight = null;
    }
  },

  retryBootstrapSession: async () => {
    const now = Date.now();
    if (now - lastBootstrapRetryAt < MIN_BOOTSTRAP_RETRY_INTERVAL_MS) {
      return;
    }
    lastBootstrapRetryAt = now;
    set({ bootstrapServerError: false, bootstrapSupportCode: null, sessionLoading: true });
    await useAuthStore.getState().bootstrapSession();
  },

  signIn: async (email: string, password: string) => {
    set({ loading: true });
    try {
      const { data } = await api.post<ILoginResponse>('/auth/login', {
        email,
        password,
      });

      if (AUTH_USE_COOKIES) {
        clearTokens();
      } else if (data.accessToken) {
        setTokens(data.accessToken, data.refreshToken);
      }

      if (AUTH_USE_COOKIES) {
        try {
          await fetchCsrfToken();
        } catch (csrfError) {
          console.error('Erro ao obter CSRF token após login:', csrfError);
        }
      }

      clearSessionLoggedOut();

      if (data.sessionId) {
        persistActivitySessionId(data.sessionId);
      }

      const user = await fetchCurrentUser();
      persistActivitySessionId(user.sessionId ?? data.sessionId);
      set({ authenticated: true, user });
    } catch (error) {
      throw parseAuthError(error);
    } finally {
      set({ loading: false });
    }
  },

  signOut: async () => {
    set({ loading: true });
    markSessionLoggedOut();
    try {
      await logoutSession();
    } finally {
      teardownLocalSession();
    }
  },

  signUp: async (userData: IApiPayload) => {
    const response = await api.post<IRegisterResponse>('/customers/register', userData);
    return response.data;
  },
}));

/**
 * `customerId` e `planFeatures` são resolvidos por posto no backend, então trocar
 * o posto ativo exige novo `GET /users/me` — o axios já envia o X-Customer-Id novo.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('sp-active-customer-changed', () => {
    void useAuthStore.getState().refreshCurrentUser();
  });
}

/** Sessão válida: estado do store (cookies) ou tokens locais (Bearer). */
export const hasActiveSession = (): boolean => {
  const { authenticated, user } = useAuthStore.getState();
  if (authenticated && user) {
    return true;
  }
  return hasStoredAccessToken() || hasStoredRefreshToken();
};
